/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: hooks/useAgentForm.ts (Agents)
 🗓️ Created: 2025-01-15
 🧩 Description:
    Custom hook for managing Agent form state, validation, and submission.
    Handles create, update, and duplicate operations.

────────────────────────────────────────────────────────────────*/

import { useState, useCallback, useMemo, useRef, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useNotifications } from '../../../contexts/NotificationContext';
import { useI18n } from '../../../hooks/useI18n';
import { createAgent, updateAgent } from '../api/agentsApi';
import { FormState } from '../types/agents.types';
import { getDefaultFormState } from '../utils/formDefaults';
import { validateForm } from '../utils/validators';
import { buildSettingsFromFormState } from '../utils/settingsHelpers';

/**
 * Hook for managing Agent form
 */
export function useAgentForm(
  id: string | undefined,
  duplicateId: string | null,
  initialMappedData: { formState: FormState; extraSettings: Record<string, unknown> } | null
) {
  const location = useLocation();
  const { showSuccess, showError, showInfo } = useNotifications();
  const { t } = useI18n();

  const isEditMode = id !== 'new';
  const defaultFormState = useMemo(() => getDefaultFormState(), []);
  const [formState, setFormState] = useState<FormState>(defaultFormState);
  const [originalFormState, setOriginalFormState] = useState<FormState>(defaultFormState);
  const [extraSettings, setExtraSettings] = useState<Record<string, unknown>>({});
  const [originalExtraSettings, setOriginalExtraSettings] = useState<Record<string, unknown>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSaving, setIsSaving] = useState(false);
  const initializedRef = useRef(false);
  const lastInitialDataRef = useRef<string | null>(null);
  const lastIdRef = useRef<string | undefined>(undefined);
  const lastLocationKeyRef = useRef<string>('');

  // Reset form when id changes
  useEffect(() => {
    if (lastIdRef.current !== id) {
      lastIdRef.current = id;
      initializedRef.current = false;
      lastInitialDataRef.current = null;
    }
  }, [id]);

  // Initialize form data when initialMappedData changes
  useEffect(() => {
    const locationKey = location.key || location.pathname + location.search;
    const locationChanged = lastLocationKeyRef.current !== locationKey;

    if (initialMappedData) {
      const dataStr = JSON.stringify(initialMappedData);
      const lastDataStr = lastInitialDataRef.current;

      // Always update if:
      // 1. Data changed (different JSON string)
      // 2. Not initialized yet
      // 3. Location changed (navigation occurred, e.g., "Save and New")
      const shouldUpdate = dataStr !== lastDataStr || !initializedRef.current || locationChanged;

      if (shouldUpdate) {
        lastInitialDataRef.current = dataStr;
        lastLocationKeyRef.current = locationKey;
        setFormState({ ...initialMappedData.formState });
        setOriginalFormState(duplicateId ? getDefaultFormState() : { ...initialMappedData.formState });
        setExtraSettings({ ...initialMappedData.extraSettings });
        setOriginalExtraSettings(duplicateId ? {} : { ...initialMappedData.extraSettings });
        setErrors({});
        initializedRef.current = true;
      }
    } else if (!isEditMode && !duplicateId) {
      // Reset refs when navigating to new form to ensure clean state
      // If location changed, force reset even if already initialized
      if (locationChanged) {
        initializedRef.current = false;
        lastInitialDataRef.current = null;
      }

      // New form - initialize with defaults
      if (!initializedRef.current) {
        lastLocationKeyRef.current = locationKey;
        const defaultState = getDefaultFormState();
        setFormState({ ...defaultState });
        setOriginalFormState({ ...defaultState });
        setExtraSettings({});
        setOriginalExtraSettings({});
        setErrors({});
        initializedRef.current = true;
      }
    }
  }, [initialMappedData, isEditMode, duplicateId, location]);

  const handleFieldChange = useCallback(<K extends keyof FormState>(field: K, value: FormState[K]) => {
    setFormState((prev) => ({ ...prev, [field]: value }));
  }, []);

  const handleExtraSettingsChange = useCallback((settings: Record<string, unknown>) => {
    setExtraSettings(settings);
  }, []);

  const hasChanges = useMemo(() => {
    const formChanged = Object.keys(formState).some((key) => {
      const formKey = key as keyof FormState;
      return formState[formKey] !== originalFormState[formKey];
    });

    const settingsChanged = JSON.stringify(extraSettings) !== JSON.stringify(originalExtraSettings);

    return formChanged || settingsChanged;
  }, [formState, extraSettings, originalFormState, originalExtraSettings]);

  // Handle save - Always-Active Save Standard: always attempt to save
  const handleSave = useCallback(async () => {
    const trimmedName = formState.agentName.trim();
    const trimmedExtension = formState.extension.trim();

    if (!trimmedName) {
      const error = new Error(t('agents.messages.validationError'));
      showError(error.message);
      throw error;
    }

    if (!trimmedExtension) {
      const error = new Error(t('agents.messages.extensionRequired'));
      showError(error.message);
      throw error;
    }

    setIsSaving(true);
    try {
      const settings = buildSettingsFromFormState(formState);
      const mergedSettings = { ...settings, ...extraSettings };

      const payload = {
        name: trimmedName,
        extension: trimmedExtension,
        contact: null,
        status: formState.initialStatus,
        ready: true,
        enabled: formState.enabled,
        settings: mergedSettings
      };

      const result = isEditMode
        ? await updateAgent(id!, payload)
        : await createAgent(payload);

      showSuccess(t('agents.messages.saveSuccess'));

      // Update original state
      setOriginalFormState({ ...formState });
      setOriginalExtraSettings({ ...extraSettings });

      // Return saved data for navigation
      return {
        id: result?.id || id,
        uuid: result?.uuid || result?.id || id,
        ...result
      };
    } catch (error) {
      const message = error instanceof Error ? error.message : t('agents.messages.saveError');
      showError(message);
      throw error;
    } finally {
      setIsSaving(false);
    }
  }, [formState, extraSettings, id, isEditMode, hasChanges, showError, showSuccess, showInfo, t]);

  const handleCancel = useCallback(() => {
    if (isEditMode) {
      setFormState({ ...originalFormState });
      setExtraSettings({ ...originalExtraSettings });
      setErrors({});
      return;
    }

    const defaultState = getDefaultFormState();
    setFormState(defaultState);
    setOriginalFormState({ ...defaultState });
    setExtraSettings({});
    setOriginalExtraSettings({});
    setErrors({});
  }, [isEditMode, originalFormState, originalExtraSettings]);

  return {
    formState,
    extraSettings,
    errors,
    isSaving,
    hasChanges,
    handleFieldChange,
    handleExtraSettingsChange,
    handleSave,
    handleCancel
  };
}

